import { useEffect, useMemo, useRef, useState } from 'react';
import { motion, useMotionValue, useSpring, useTransform, useScroll } from 'framer-motion';

// Cinematic hero background — Ken Burns zoom, scroll parallax, mouse tilt,
// soft haze layer and floating weld-spark particles. Only the sparks and
// mouse tilt are enabled on desktop (coarse pointer / prefers-reduced-motion
// disable them automatically). Sits absolutely behind the hero content.
export default function CinematicHeroBackground({ src, alt = '', className = '', priority = true }) {
  const wrapRef = useRef(null);
  const [isCoarse, setIsCoarse] = useState(false);
  const [reduceMotion, setReduceMotion] = useState(false);

  // Mouse parallax
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const smx = useSpring(mx, { stiffness: 60, damping: 20, mass: 0.6 });
  const smy = useSpring(my, { stiffness: 60, damping: 20, mass: 0.6 });
  const imgX = useTransform(smx, (v) => v * -14);
  const imgY = useTransform(smy, (v) => v * -10);
  const hazeX = useTransform(smx, (v) => v * 40);
  const hazeY = useTransform(smy, (v) => v * 26);

  // Scroll parallax on the wrapper
  const { scrollYProgress } = useScroll({ target: wrapRef, offset: ['start start', 'end start'] });
  const scrollY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const scrollScale = useTransform(scrollYProgress, [0, 1], [1, 1.08]);

  useEffect(() => {
    setIsCoarse(window.matchMedia('(pointer: coarse)').matches);
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReduceMotion(mq.matches);
    const onChange = () => setReduceMotion(mq.matches);
    mq.addEventListener?.('change', onChange);
    return () => mq.removeEventListener?.('change', onChange);
  }, []);

  useEffect(() => {
    if (isCoarse || reduceMotion) return;
    const onMove = (e) => {
      const w = window.innerWidth;
      const h = window.innerHeight;
      mx.set(((e.clientX - w / 2) / (w / 2)));
      my.set(((e.clientY - h / 2) / (h / 2)));
    };
    window.addEventListener('mousemove', onMove);
    return () => window.removeEventListener('mousemove', onMove);
  }, [isCoarse, reduceMotion, mx, my]);

  // Sparks — 14 particles with deterministic randomness
  const sparks = useMemo(() => {
    const rand = (seed) => {
      const x = Math.sin(seed) * 10000;
      return x - Math.floor(x);
    };
    return Array.from({ length: 14 }, (_, i) => ({
      id: i,
      left: `${rand(i + 1) * 100}%`,
      size: 1 + rand(i + 7) * 2.2,
      dur: 7 + rand(i + 3) * 6,
      delay: rand(i + 5) * 8,
      driftX: (rand(i + 11) - 0.5) * 40,
      startY: 80 + rand(i + 13) * 20,
    }));
  }, []);

  const enableSparks = !isCoarse && !reduceMotion;

  return (
    <div ref={wrapRef} className={`absolute inset-0 overflow-hidden ${className}`} aria-hidden={alt === ''}>
      {/* Image with Ken Burns + scroll + mouse parallax */}
      <motion.div style={{ y: scrollY, scale: scrollScale }} className="absolute inset-0 will-change-transform">
        <motion.div
          style={{ x: imgX, y: imgY }}
          className="absolute -inset-8"
        >
          <motion.img
            src={src}
            alt={alt}
            loading={priority ? 'eager' : 'lazy'}
            fetchpriority={priority ? 'high' : 'auto'}
            className="w-full h-full object-cover"
            style={{ objectPosition: 'center 30%', filter: 'grayscale(15%) contrast(1.06) saturate(1.02)' }}
            initial={{ scale: 1.05 }}
            animate={reduceMotion ? { scale: 1.05 } : { scale: [1.05, 1.14, 1.05] }}
            transition={reduceMotion ? undefined : { duration: 26, ease: 'easeInOut', repeat: Infinity }}
          />
        </motion.div>
      </motion.div>

      {/* Dynamic vignette + gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/40 to-black" />
      <motion.div
        className="absolute inset-0"
        style={{
          x: hazeX,
          y: hazeY,
          background:
            'radial-gradient(ellipse 65% 55% at 30% 55%, rgba(255,255,255,0.08), transparent 60%), radial-gradient(ellipse 55% 45% at 80% 40%, rgba(255,220,180,0.05), transparent 65%)',
          mixBlendMode: 'screen',
        }}
      />
      {/* Very subtle drifting haze */}
      {!reduceMotion && (
        <motion.div
          className="absolute -inset-32 opacity-40"
          style={{
            background: 'radial-gradient(circle at 50% 50%, rgba(255,255,255,0.06) 0%, transparent 55%)',
            filter: 'blur(60px)',
            mixBlendMode: 'screen',
          }}
          animate={{ x: [0, 40, -20, 0], y: [0, -30, 20, 0] }}
          transition={{ duration: 22, repeat: Infinity, ease: 'easeInOut' }}
        />
      )}

      {/* Architectural grid */}
      <div className="absolute inset-0 grid-bg-fine opacity-35" />

      {/* Weld sparks — desktop only */}
      {enableSparks && (
        <div className="pointer-events-none absolute inset-0 hidden md:block" style={{ mixBlendMode: 'screen' }}>
          {sparks.map((s) => (
            <motion.span
              key={s.id}
              className="absolute rounded-full"
              style={{
                left: s.left,
                bottom: `${s.startY}%`,
                width: s.size,
                height: s.size,
                background: 'radial-gradient(circle, #fff2c8 0%, rgba(255,200,120,0.7) 40%, rgba(255,140,60,0) 80%)',
                boxShadow: '0 0 6px rgba(255,220,150,0.7)',
              }}
              initial={{ opacity: 0, y: 0, x: 0 }}
              animate={{
                opacity: [0, 1, 1, 0],
                y: [0, -160, -320, -480],
                x: [0, s.driftX * 0.3, s.driftX * 0.7, s.driftX],
              }}
              transition={{ duration: s.dur, repeat: Infinity, delay: s.delay, ease: 'easeOut' }}
            />
          ))}
        </div>
      )}
    </div>
  );
}
